import { useState, useRef, useEffect, useLayoutEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface DropdownProps {
  trigger: (open: boolean) => ReactNode;
  children: ReactNode;
  align?: 'left' | 'right';
  panelClassName?: string;
  /** Close the panel on any click inside it — correct for a menu of
   *  click-and-close action buttons, wrong for interactive controls like
   *  checkboxes. Default true preserves existing behavior everywhere. */
  closeOnInnerClick?: boolean;
}

interface Position {
  top: number;
  left?: number;
  right?: number;
}

export function Dropdown({ trigger, children, align = 'left', panelClassName, closeOnInnerClick = true }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<Position | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Position the portaled panel against the trigger's viewport rect —
  // avoids being clipped by ancestors with overflow:hidden (e.g. table-wrap).
  useEffect(() => {
    if (!open) return;
    function updatePosition() {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPos(
        align === 'right'
          ? { top: rect.bottom + 6, right: window.innerWidth - rect.right }
          : { top: rect.bottom + 6, left: rect.left }
      );
    }
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, align]);

  // Keep the panel on screen: a menu opened near the right edge (e.g. the
  // last column's filter) moves left, and one with no room below opens
  // above its button instead (or moves up as far as it fits).
  useLayoutEffect(() => {
    const el = panelRef.current;
    if (!open || !pos || !el) return;
    // `translate`, not `transform`: the panel's fade-in animation owns transform.
    el.style.translate = '';
    const r = el.getBoundingClientRect();
    const m = 8;
    let dx = 0, dy = 0;
    if (r.right > window.innerWidth - m) dx = window.innerWidth - m - r.right;
    if (r.left + dx < m) dx = m - r.left;
    if (r.bottom > window.innerHeight - m) {
      const t = triggerRef.current?.getBoundingClientRect();
      const above = t ? t.top - 6 - r.height : -1;
      dy = above >= m ? above - r.top : Math.max(m - r.top, window.innerHeight - m - r.bottom);
    }
    el.style.translate = dx || dy ? `${dx}px ${dy}px` : '';
  }, [open, pos]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <>
      <div ref={triggerRef} onClick={() => setOpen(o => !o)}>{trigger(open)}</div>
      {open && pos && createPortal(
        <div
          ref={panelRef}
          className={`dropdown-panel${panelClassName ? ` ${panelClassName}` : ''}`}
          style={{ position: 'fixed', top: pos.top, left: pos.left, right: pos.right }}
          // With closeOnInnerClick off, a button marked data-dropdown-close
          // (e.g. a filter's OK / Cancel) still closes the panel.
          onClick={e => {
            if (closeOnInnerClick || (e.target as HTMLElement).closest('[data-dropdown-close]')) setOpen(false);
          }}
        >
          {children}
        </div>,
        document.body,
      )}
    </>
  );
}
